import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import '../../Accordion/Accordion.scss';
import down from '../../../../img/icons/Delta/down.png';
import { Modal } from 'react-bootstrap';

interface IProps {
    children: React.ReactNode;
    title: string;
    expandedHide?: boolean;
    expandedInit: boolean;
    nombreError?: string;
    modal?: string;
    totalSize?: number | null;
    maxSize?: number | null;
}

const AccordionForm: React.FC<IProps> = ({ children, title, expandedHide, expandedInit, nombreError, modal, totalSize, maxSize }) => {
    const { formState: { errors } } = useFormContext();
    const [accordionState, setAccordionState] = useState<boolean>(expandedInit);
    const [show, setShow] = useState(false);
    const [showDocumentSizePercentage, setShowDocumentSizePercentage] = useState(false);
    const canShowDocumentSize = typeof totalSize === 'number' && typeof maxSize === 'number' && maxSize > 0;
    const documentSizePercentage = canShowDocumentSize ? Math.max((totalSize / maxSize) * 100, 0) : 0;
    const documentSizeFillPercentage = Math.min(documentSizePercentage, 100);
    const exceededDocumentSize = canShowDocumentSize ? Math.max(totalSize - maxSize, 0) : 0;
    const documentSizeState = totalSize && maxSize && totalSize > maxSize
        ? 'exceeded'
        : documentSizePercentage >= 90
            ? 'critical'
            : documentSizePercentage >= 75
                ? 'warning'
                : 'available';

    useEffect(() => {
        if (typeof expandedHide === 'boolean') {
            setAccordionState(false);

        }
    }, [expandedHide]);

    const handleToggleAccordionState = () => {
        setAccordionState(!accordionState);
    };

    useEffect(() => {

        if (Object.keys(errors).some(key => key.startsWith(nombreError))) {
            setAccordionState(true);
        }
    }, [errors[nombreError]]);



    return (
        <div className={`Accordion ${accordionState ? 'expanded' : ''}`}>
            <div className={`accordion-header-block `}>
                <div
                    role="button"
                    tabIndex={0}
                    onClick={handleToggleAccordionState}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleToggleAccordionState()}
                    aria-expanded={accordionState}
                    aria-controls={`accordion-content-${title.replace(/\s+/g, '-')}`}
                    className="accordion-toggle-btn"
                >
                    <span className="header-text d-flex align-items-center">
                        <span className="e-icons MT_Preview mr-2"></span>
                        <span className="text">{title}</span>
                    </span>
                    {canShowDocumentSize && (
                        <button
                            type="button"
                            className={`accordion-document-size accordion-document-size--${documentSizeState}`}
                            onClick={(event) => {
                                event.stopPropagation();
                                setShowDocumentSizePercentage((currentValue) => !currentValue);
                            }}
                            onKeyDown={(event) => event.stopPropagation()}
                            aria-label={`Documentación subida: ${totalSize.toFixed(1)} de ${maxSize.toFixed(1)} MB. Pulsar para mostrar ${showDocumentSizePercentage ? 'megabytes' : 'porcentaje'}`}
                        >
                            <span
                                className="accordion-document-size__track"
                                role="progressbar"
                                aria-label="Espacio utilizado para documentación"
                                aria-valuemin={0}
                                aria-valuemax={maxSize}
                                aria-valuenow={Math.min(totalSize, maxSize)}
                                aria-valuetext={`${totalSize.toFixed(1)} de ${maxSize.toFixed(1)} MB`}
                            >
                                <span
                                    className="accordion-document-size__progress"
                                    style={{ width: `${documentSizeFillPercentage}%` }}
                                />
                                <span className="accordion-document-size__threshold" aria-hidden="true" />
                            </span>
                            <span className="accordion-document-size__details">
                                <span className="accordion-document-size__values">
                                    <strong>
                                        {showDocumentSizePercentage
                                            ? `${Math.round(documentSizePercentage)}%`
                                            : `${totalSize.toFixed(1)} MB`}
                                    </strong>
                                    <span aria-hidden="true">/</span>
                                    <small>{maxSize.toFixed(0)} MB máx.</small>
                                </span>
                                {exceededDocumentSize > 0 && (
                                    <small className="accordion-document-size__excess">
                                        +{exceededDocumentSize.toFixed(1)} MB sobre el límite
                                    </small>
                                )}
                            </span>
                        </button>
                    )}
                    <div className='d-flex align-items-center gap-2'>
                        {modal && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShow(true);
                                }}
                                aria-label={`Información sobre ${title}`}
                                className="rounded-circle p-2 d-inline-flex justify-content-center align-items-center position-relative z-3 accordion-modal"
                            >
                                <div className="information-icon-orange p-0" aria-hidden="true" />
                            </button>
                        )}
                        <span className="header-icon e-icons e-down-arrow2">
                            <img src={down} alt="" aria-hidden="true" className='iconDown' />
                        </span>
                    </div>
                </div>
            </div>
            <div className={`accordion-item`}>
                <div
                    id={`accordion-content-${title.replace(/\s+/g, '-')}`}
                    className={`${accordionState ? '' : 'd-none'}`}
                    role="region"
                    aria-label={title}
                >
                    {children}
                </div>
            </div>


            <Modal size='lg' show={show} onHide={() => setShow(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{modal}</Modal.Body>
            </Modal>
        </div>


    );
};

export default AccordionForm;
